import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useColumnResize } from '@/hooks/useColumnResize';
import { useColumnFilters } from '@/hooks/useColumnFilters';
import ColumnFilterInput from './ColumnFilterInput';
import { cn } from '@/lib/utils';
import { X, Filter } from 'lucide-react';

/**
 * DataTable — reusable table with column resize, inline filters, visible borders
 *
 * @param {Object} props
 * @param {Array} props.columns — column definitions [{key, label, width, filterType, align, render, ...}]
 * @param {Array} props.data — row objects
 * @param {Function} [props.onRowClick] — (row) => void
 * @param {string} props.storageKey — localStorage key for persisting widths
 * @param {string} [props.emptyMessage] — shown when data is empty
 * @param {string} [props.className] — wrapper class
 * @param {Function} [props.onColumnReorder] — (fromKey, toKey) => void — callback for column reordering
 */
const DataTable = ({
  columns,
  data,
  onRowClick,
  storageKey,
  emptyMessage = 'Нет данных',
  className,
  onColumnReorder,
}) => {
  const {
    getColumnWidth,
    resizingColumn,
    totalWidth,
    handleResizeStart,
    handleDoubleClick,
  } = useColumnResize(columns, storageKey);

  const {
    filters,
    setFilter,
    clearAllFilters,
    applyFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useColumnFilters();

  // Column drag-and-drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragNodeRef = useRef(null);

  const handleColumnDragStart = useCallback((e, colKey) => {
    if (!onColumnReorder) return;
    setDraggedColumn(colKey);
    const th = e.target.closest('th');
    dragNodeRef.current = th;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  }, [onColumnReorder]);

  const handleColumnDragEnd = useCallback(() => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
    dragNodeRef.current = null;
  }, []);

  const handleColumnDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== colKey) {
      setDragOverColumn(colKey);
    }
  }, [draggedColumn]);

  const handleColumnDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetKey && onColumnReorder) {
      onColumnReorder(draggedColumn, targetKey);
    }
    handleColumnDragEnd();
  }, [draggedColumn, onColumnReorder, handleColumnDragEnd]);

  // Apply column filters on top of already-filtered data
  const filteredData = useMemo(() => {
    return applyFilters(data, columns);
  }, [data, columns, applyFilters]);

  // Check if any column has a filter type
  const hasFilterableColumns = columns.some(col => col.filterType && col.filterType !== 'none');

  return (
    <div
      className={cn("overflow-auto relative", className)}
      style={{ maxHeight: 'calc(100vh - 240px)' }}
    >
      <table
        className="text-sm"
        style={{ tableLayout: 'fixed', width: totalWidth, minWidth: '100%', borderSpacing: 0 }}
      >
        {/* Header row */}
        <thead className="sticky top-0 z-10 bg-white shadow-sm">
          <tr>
            {columns.map((col, idx) => (
              <th
                key={col.key}
                draggable={!!onColumnReorder}
                onDragStart={(e) => handleColumnDragStart(e, col.key)}
                onDragEnd={handleColumnDragEnd}
                onDragOver={(e) => handleColumnDragOver(e, col.key)}
                onDragEnter={(e) => { e.preventDefault(); if (draggedColumn) setDragOverColumn(col.key); }}
                onDragLeave={() => setDragOverColumn(null)}
                onDrop={(e) => handleColumnDrop(e, col.key)}
                className={cn(
                  "py-1.5 px-2 font-medium text-xs text-gray-500 bg-gray-50 relative group border-b border-r border-gray-300",
                  col.align === 'right' && 'text-right',
                  col.align === 'center' && 'text-center',
                  idx === 0 && 'border-l border-gray-300',
                  resizingColumn === col.key && 'bg-blue-50',
                  onColumnReorder && 'cursor-grab active:cursor-grabbing select-none',
                  dragOverColumn === col.key && 'bg-blue-100 border-l-2 border-l-blue-500',
                  draggedColumn === col.key && 'opacity-50'
                )}
                style={{ width: getColumnWidth(col.key) }}
              >
                <span className="block truncate pr-3">{col.label}</span>

                {/* Resize handle — wide hit area with visible line */}
                <div
                  className="absolute top-0 -right-[5px] w-[10px] h-full cursor-col-resize z-20 group/resize"
                  onMouseDown={(e) => handleResizeStart(e, col.key)}
                  onDoubleClick={(e) => handleDoubleClick(e, col.key)}
                  onClick={(e) => e.stopPropagation()}
                  title="Потяните для изменения ширины. Двойной клик — сброс"
                >
                  {/* Visible line */}
                  <div
                    className={cn(
                      "absolute left-1/2 -translate-x-1/2 top-0 w-[3px] h-full rounded-full transition-colors",
                      resizingColumn === col.key
                        ? "bg-blue-500"
                        : "bg-gray-200 group-hover/resize:bg-blue-400"
                    )}
                  />
                </div>
              </th>
            ))}
          </tr>

          {/* Filter row */}
          {hasFilterableColumns && (
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={`filter-${col.key}`}
                  className={cn(
                    "py-1 px-1 font-normal bg-gray-50/80 border-b border-r border-gray-300",
                    idx === 0 && 'border-l border-gray-300'
                  )}
                  style={{ width: getColumnWidth(col.key) }}
                >
                  {col.filterType && col.filterType !== 'none' ? (
                    <ColumnFilterInput
                      column={col}
                      value={filters[col.key]}
                      onChange={(val) => setFilter(col.key, val)}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          )}

          {/* Active filters indicator */}
          {hasActiveFilters && (
            <tr className="border-b bg-blue-50">
              <td colSpan={columns.length} className="py-1 px-3">
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <Filter className="w-3 h-3" />
                  <span>Фильтры ({activeFilterCount}): показано {filteredData.length} из {data.length}</span>
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2"
                  >
                    <X className="w-3 h-3" />
                    Сбросить все
                  </button>
                </div>
              </td>
            </tr>
          )}
        </thead>

        {/* Body */}
        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-8 text-center text-gray-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            filteredData.map((row, rowIdx) => (
              <tr
                key={row.id || rowIdx}
                className={cn(
                  "hover:bg-gray-50 transition-colors",
                  onRowClick && "cursor-pointer"
                )}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col, idx) => (
                  <td
                    key={col.key}
                    className={cn(
                      "py-2.5 px-2 overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200",
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      idx === 0 && 'border-l border-gray-200'
                    )}
                    style={{ width: getColumnWidth(col.key) }}
                  >
                    {col.render
                      ? col.render(row[col.key], row)
                      : (row[col.key] ?? '—')
                    }
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
